<template>
  <page>
    <sidebar slot="sidebar" />
    <span slot="header">Knowledge Check: {{ topic.name }}</span>
    <span slot="breadcrumbs">
      <b-breadcrumb :items="breadcrumbs"/>
      <div v-if="model" class="d-flex">
        <router-link v-if="id" :to="{name: 'admin-knowledgecheck-preview', params: {topic: topic.id, id: id}}">
          <i class="fas fa-exchange-alt"></i> Preview
        </router-link>
        <div class="flex-grow-1"></div>
        <select-questions :id="model.id"></select-questions>
      </div>
    </span>

    <div v-if="!fetching && !model" class="text-center">
      <b-button
        :disabled="disabled"
        variant="primary"
        @click.stop="create()"
      >
        Create Knowledge Check
      </b-button>
    </div>

    <div v-if="model">
      <instructions :kc="model" @updated="onInstructionsUpdated"></instructions>
      <br />
      <div
        v-for="(q, i) in model.questions"
        :key="q.id"
        :class="{'border': q== selectedQuestion, 'question-hover': !selectedQuestion}"
        class="d-flex p-2 question"
        @click.stop="onQuestionSelected(q)"
      >
        <div class="question-label">{{ i + 1 }}.</div>
        <div class="flex-fill question-content">
          <edit-question
            v-if="q == selectedQuestion"
            v-model="model.questions[i]"
            :knowledgecheck="model"
            @saved="onQuestionSaved(i, $event)"
            @canceled="onQuestionCanceled(i, $event)"
          ></edit-question>
          <view-question
            v-if="q != selectedQuestion"
            v-model="model.questions[i]"
            @selected="onQuestionSelected"
          ></view-question>
        </div>
        <div v-if=" !selectedQuestion" class="nowrap" style="white-space:nowrap">
          <b-button :disabled="disabled" variant="outline-secondary" size="md" title="edit question" @click="onQuestionSelected(q)"><i class="fa fa-edit" ></i></b-button>
          <b-button :disabled="disabled" variant="outline-secondary" size="md" title="delete question" @click.stop="confirmQuestionDelete(q)"><i class="fa fa-trash" ></i></b-button>
          <b-button v-if="canMoveUp(q)" :disabled="disabled" title="move up" variant="outline-secondary" @click.stop="moveUp(q)"><i class="fa fa-sm fa-arrow-up" title="move up"></i></b-button>
          <b-button v-if="canMoveDown(q)" :disabled="disabled" title="move-down" variant="outline-secondary" @click.stop="moveDown(q)"><i class="fa fa-sm fa-arrow-down" title="move down"></i></b-button>
        </div>
      </div>

      <div v-if="!selectedQuestion">
        <b-link :disabled="disabled" type="link" @click.stop="addQuestion()">+ Add Question</b-link>
      </div>
    </div>

    <b-modal
      v-model="showConfirmDelete"
      title="Delete Question"
      centered
      size="md"
      ok-title="Delete Question"
      ok-variant="danger"
      cancel-title="Cancel"
      @hidden="cancelDeleteQuestion"
      @ok="deleteQuestion"
    >
      <p>This will permanently delete this question. Are you sure you want to continue?</p>
    </b-modal>
  </page>
</template>

<script>
import _ from 'lodash'
import sidebar from '../../../sidebar'
import page from '../../../templates/page'
import api from '@/api/knowledgechecks/knowledgecheck.js'
import questionApi from '@/api/knowledgechecks/questions.js'

import kcBase from './kc-base'
import instructions from './instructions'
import editQuestion from './questions/edit'
import viewQuestion from './questions/view'
import deleteQuestion from './questions/deleteModal'

import kcForm from '@/components/pages/client/knowledge-checks/form'

import selectQuestions from './print/select-questions'

export default {
  components: {
    deleteQuestion,
    editQuestion,
    viewQuestion,
    instructions,
    kcForm,
    page,
    sidebar,
    selectQuestions
  },
  mixins: [kcBase],
  data() {
    return {
      model: null,
      saving: false,
      fetching: false,
      isLoaded: false,
      selectedQuestion: null,
      showConfirmDelete: false,
      questionToDelete: null,
      breadcrumbs: [
        {
          text: 'Modules',
          to: {
            name: 'admin-modules'
          }
        },
        {
          text: 'Components',
          to: {
            name: 'admin-components'
          }
        },
        {
          text: 'Topics',
          to: {
            name: 'admin-topics'
          }
        },
        {
          text: 'Knowledge Check',
          active: true
        }
      ]
    }
  },
  computed: {
    id() {
      return this.model ? this.model.id : this.$route.params.id || null
    },
    params(){
      return {tenantId : this.organization.id,topicId: this.topic.id}
    },
    disabled(){
      return this.fetching || this.saving
    }
  },
  async mounted() {
    this.fetching = true
    await (this.id ? this.load(this.id) : this.loadDefault())
    this.fetching = false
  },
  methods: {
    loadDefault() {
      return api.index(this.params).then(response => {
        this.model = response.data.data
        this.setIsLoaded()
      })
    },
    load(id) {
      const params = _.clone(this.params)
      params.knowledgecheckId = this.id

      return api.show(params).then(response => {
        this.model = response.data.data
        this.setIsLoaded()
      })
    },
    setIsLoaded() {
      if (this.model) {
        this.model.questions = this.model.questions.sort(
          (a, b) => a.order - b.order
        )
      }
      this.$nextTick(() => {
        this.isLoaded = true
      })
    },
    async create() {
      this.fetching = true

      const params = _.clone(this.params)
      params.data = {
        instructions: 'new knowledge check'
      }

      const response = await api.store(params)
      this.model = response.data.data
      this.model.questions = this.model.questions || []
      this.setIsLoaded()

      this.fetching = false
    },
    addQuestion() {
      const q = {
        type: 'radio',
        order: this.model.questions.length + 1,
        text: 'default text',
        choices: [
          {
            label: 'Choice 1',
            value: '1',
            is_answer: true,
            order: 1
          },
          {
            label: 'Choice 2',
            value: '2',
            is_answer: false,
            order: 2
          }
        ]
      }

      this.model.questions.push(q)
      this.selectedQuestion = q
    },

    onQuestionSaved(idx, q) {
      this.model.questions[idx] = q
      this.selectedQuestion = null
    },
    onQuestionCanceled(idx, q) {
      this.selectedQuestion = null

      if (!q.id) {
        this.model.questions.splice(idx, 1)
      }
    },
    onQuestionSelected(q){
      if(this.selectedQuestion && !this.selectedQuestion.id){
        //adding a new question that hasn't been saved yet so we can't leave
        return
      }
      this.selectedQuestion = q
    },
    onInstructionsUpdated(model) {
      this.model.instructions = model.instructions
      this.model.id = model.id
    },
    confirmQuestionDelete(q){
      this.showConfirmDelete = true
      this.questionToDelete = q
    },
    cancelDeleteQuestion(){
      this.showConfirmDelete = false
      this.questionToDelete = null
    },
    async deleteQuestion() {
      this.saving  = true

      const params = _.clone(this.params)
      params.knowledgecheckId = this.id
      params.questionId = this.questionToDelete.id

      await questionApi.destroy(params)
      this.model.questions.splice(this.model.questions.indexOf(this.questionToDelete),1)

      this.saving = false
      this.questionToDelete = null
      this.showConfirmDelete = false
    },
    async moveUp(question) {
      this.fetching = true
      const idx = this.model.questions.indexOf(question)
      this.model.questions.splice(idx, 1)
      this.model.questions.splice(idx - 1, 0, question)
      await this.setQuestionSortOrder()
      this.fetching = false
    },
    async moveDown(question) {
      this.fetching = true
      const idx = this.model.questions.indexOf(question)
      this.model.questions.splice(idx, 1)
      this.model.questions.splice(idx + 1, 0, question)
      await this.setQuestionSortOrder()
      this.fetching = false
    },
    async setQuestionSortOrder() {
      _.forEach(this.model.questions, function(q, idx) {
        q.order = idx + 1
      })

      const params = _.clone(this.params)
      params.knowledgecheckId = this.id
      params.data = _.map(this.model.questions, function(q) {
        return { id: q.id, order: q.order }
      })
      await questionApi.order(params)
    },
    canMoveUp(question) {
      if (!this.model.questions) {
        return false
      }
      return (
        !this.model.questions.length == 0 && question != this.model.questions[0]
      )
    },
    canMoveDown(question) {
      if (!this.model.questions) {
        return false
      }

      return (
        !this.model.questions.length == 0 &&
        question != this.model.questions[this.model.questions.length - 1]
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import "assets/stylesheets/main.scss";

.question-hover:hover {
  background-color: rgba(0, 0, 0, 0.075);
  cursor: pointer;
}

.question-label {
  margin-left: 1rem;
  min-width: 1.25rem;
  white-space: nowrap !important;
}

.question-content {
  padding: 0 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/admin/knowledge-checks/edit.vue